import * as React from "react";
import Card from '@material-ui/core/Card';
import { Form } from 'react-final-form';
import { makeStyles } from "@material-ui/core/styles";
import { Box, Button, CardActions, CircularProgress, Theme, Typography } from "@material-ui/core";
import {
    BooleanInput,
    choices,
    Notification,
    PasswordInput,
    required, useNotify,
    useRedirect,
    useTranslate,
} from "react-admin";
import StandardApiError from "shared/errors/standardApiError";
import { useSafeSetState } from "ra-core";
import { mutateAcceptInvitation } from "api/mutations/userAcceptInvitation";
import { useLocation } from "react-router-dom";

function useQuery() {
    const { search } = useLocation();

    return React.useMemo(() => new URLSearchParams(search), [search]);
}

const useStyles = makeStyles(
    (theme: Theme) => ({
        main: {
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            alignItems: 'center',
            justifyContent: 'flex-start',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundImage:
                'radial-gradient(circle at 50% 14em, #313264 0%, #00023b 60%, #00023b 100%)',
        },
        card: {
            width: 300,
            marginTop: '6em',
            marginBottom: '6em',
        },
        button: {
            width: '100%',
        },
        icon: {
            marginRight: theme.spacing(1),
        },
    }),
);

const AcceptInvitation = (props: {}) => {
    const classes = useStyles(props);
    const [saving, setSaving] = useSafeSetState(false);
    const notify = useNotify();
    const redirect = useRedirect();
    const query = useQuery();
    const translate = useTranslate();

    const save = React.useCallback(
        async (values) => {
            try {
                setSaving(true);
                await mutateAcceptInvitation({
                    userId: parseInt(query.get('userId') ?? '0'),
                    token: query.get('token'),
                    ...values,
                });
                setSaving(false);
                notify('User activated', {type: 'success'});
                redirect('/login');
            } catch (error) {
                setSaving(false);
                if (error instanceof StandardApiError) {
                    error.requestErrors.forEach(message => notify(message, {type: 'error'}));
                    if (error.validationErrors.userId) {
                        notify(error.validationErrors.userId, {type: 'error'})
                    }
                    if (error.validationErrors.token) {
                        notify(error.validationErrors.token, {type: 'error'})
                    }
                    return error.validationErrors;
                }
            }
        },
        [setSaving, redirect, query, notify]
    );

    return (
        <div
            className={classes.main}
        >
            <Card className={classes.card}>
                <Form
                    onSubmit={save}
                    render={({ handleSubmit }) => (
                        <form onSubmit={handleSubmit} noValidate>
                            <Box p="1em">
                                <Typography variant="h6" gutterBottom>
                                    {translate(
                                        'Accept invitation'
                                    )}
                                </Typography>

                                <PasswordInput source="password" validate={[required()]} fullWidth disabled={saving} />
                                <BooleanInput source="isMarketingConsent" validate={[
                                    required('Accept GDPR'),
                                    choices([true], 'Accept GDPR'),
                                ]} fullWidth disabled={saving} />
                            </Box>
                            <CardActions>
                                <Button
                                    variant="contained"
                                    type="submit"
                                    color="primary"
                                    disabled={saving}
                                    className={classes.button}
                                >
                                    {saving && (
                                        <CircularProgress
                                            className={classes.icon}
                                            size={18}
                                            thickness={2}
                                        />
                                    )}
                                    {translate('Activate account')}
                                </Button>
                            </CardActions>
                        </form>
                    )}
                />
            </Card>
            <Notification />
        </div>
    );
}

export default AcceptInvitation;